import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import { SHEETS_CONFIG, SheetsConfig } from './sheets-config';

// Cache en memoria por instancia. En SSR (server) sobrevive entre requests
// mientras la función está caliente; en el browser dura lo que la pestaña.
// Evita agotar la cuota de lectura de Sheets (60/min) con cada page view.
const cacheHojas = new Map<string, { expira: number; valor: any }>();
const TTL_CACHE_MS = 10 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})
export class GooglesheetService {
  private configSheetId = '11w7u0ikCfYFrWkD72VpZ6gNA46qzLpAoW4v79gE-E8c';

  // Antes se buscaba en vivo (Configuraciones!B4 → resolver el ID → recién
  // ahí pedir los datos), una cadena de 2 pedidos secuenciales en el camino
  // crítico de CADA carga de página (~1,5s en PageSpeed). Ese ID no cambia
  // salvo que se migre a otra planilla de productos — algo que no pasó
  // nunca y que de pasar necesita tocar código igual. Si el día de mañana
  // cambia: actualizar esta constante (queda documentado en Configuraciones!
  // B4 de la planilla de config) y redeployar.
  private readonly sheetId = '1OYl79cr9q6i_5l67w4Mfs4SWlGEhXW8g776ffjabNr8';

  private hojasSubject = new ReplaySubject<string[]>(1);
  hojas$ = this.hojasSubject.asObservable();

  private readonly base: string;
  private readonly key: string;

  // La API key ya no vive en el cliente: en el browser se pega al proxy
  // same-origin `/api/sheets` y en SSR se va directo a Google con la key
  // del entorno (ver app.module.ts / app.server.module.ts).
  constructor(
    private http: HttpClient,
    @Inject(SHEETS_CONFIG) cfg: SheetsConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.base = cfg.base.replace(/\/$/, '');
    this.key = cfg.key || '';
  }

  private url(path: string): string {
    return `${this.base}${path}${this.key ? `?key=${this.key}` : ''}`;
  }

  // En SSR la API se pide al mismo proceso (no al puerto de producción
  // hardcodeado): sirve igual en 4000, en un preview o en un smoke test.
  private get apiBase(): string {
    if (isPlatformBrowser(this.platformId)) return '';
    const env = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
    return `http://127.0.0.1:${env?.['PORT'] || '4000'}`;
  }

  // Rangos de la planilla de configuración que ya se migraron a Postgres.
  // El shape que devuelve la API es el mismo `{ values }` de Sheets.
  private readonly endpointsConfig: Record<string, string> = {
    Home: 'home',
    Footer: 'footer',
    'Preguntas Frecuentes': 'faq',
    Configuraciones: 'settings',
    Cotizaciones: 'cotizaciones',
    Valores_Cotizacion: 'valores',
  };

  getSheetData(range: string, sheet: string = ""): Observable<any> {
    // Usados y Nuevos ya salen del CRM. Si la API falla, cae a la planilla
    // (que sigue existiendo como respaldo durante la transición).
    const clave = String(range).trim();
    if (clave === 'Usados' || clave === 'Nuevos' || clave === 'Accesorios') {
      const endpoint = clave === 'Usados' ? 'usados' : clave === 'Nuevos' ? 'nuevos' : 'accesorios';
      const base = this.apiBase;
      return this.http.get<any>(`${base}/api/public/catalogo/${endpoint}`).pipe(
        catchError(() => this.getData(clave, this.sheetId)),
      );
    }

    const endpoint = this.endpointsConfig[clave.split('!')[0].trim()];
    if (endpoint) {
      const base = this.apiBase;
      return this.http.get<any>(`${base}/api/public/config/${endpoint}`).pipe(
        map((data) => ({ ...data, values: this.recortarConfig(clave, data?.values || []) })),
        catchError(() => this.getData(clave, this.configSheetId)),
      );
    }

    return this.getData(range, sheet == 'config' ? this.configSheetId : this.sheetId);
  }

  // La API devuelve la tabla completa; algunos consumidores piden un rango
  // puntual (B3 solo el incremento del dólar, A2 sin la cabecera de modelos).
  private recortarConfig(rango: string, values: any[]): any[] {
    if (/^Configuraciones!B3(?::B3)?$/i.test(rango)) {
      return values[2] ? [values[2]] : [['15']];
    }
    if (/^Cotizaciones!A2/i.test(rango)) {
      return values.slice(1);
    }
    return values;
  }

  // Tabs de la planilla que existen para uso interno y no deben mostrarse como sección pública.
  private readonly hojasOcultas = ['Datos', 'Dolar'];

  private static hojasCache: { expira: number; hojas: string[] } | null = null;

  cargarHojas(): void {
    const cache = GooglesheetService.hojasCache;
    if (cache && cache.expira > Date.now()) {
      this.hojasSubject.next(cache.hojas);
      return;
    }
    const url = this.url(`/v4/spreadsheets/${this.sheetId}`);
    this.http.get<any>(url).subscribe(response => {
      const hojas = response.sheets
        .map((sheet: any) => sheet.properties.title)
        .filter((titulo: string) => !this.hojasOcultas.includes(titulo));
      GooglesheetService.hojasCache = { expira: Date.now() + 10 * 60 * 1000, hojas };
      this.hojasSubject.next(hojas);
    });
  }

  getData(range: string, sheetId: string) {
    const url = this.url(`/v4/spreadsheets/${sheetId}/values/${range}`);
    const ahora = Date.now();
    const guardado = cacheHojas.get(url);
    if (guardado && guardado.expira > ahora) return of(guardado.valor);
    return this.http.get<any>(url).pipe(
      tap(valor => cacheHojas.set(url, { expira: ahora + TTL_CACHE_MS, valor })),
    );
  }
}
